import React, { useState } from 'react';
import { App, Avatar, Button, Card, Descriptions, Form, Modal, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { UserOutlined } from '@ant-design/icons';
import RenderForm from '../../components/common/RenderForm';
import { updateInfoRequest } from '../../services/authRequest';
import { setAuthUpdate } from '../../store/ducks/user/userSlice';
import SeedPermissions from '../../components/common/SeedPermissions';

const Info = () => {
  const dispatch = useDispatch();
  const { message, notification } = App.useApp();
  const dataRedux = useSelector((state) => state.user.info);
  const [modalOpen, setModalOpen] = useState(false);
  const [formEdit] = Form.useForm();
  const checkChangePassword = Form.useWatch('change_password', formEdit);
  const avatar = Form.useWatch('avatar', formEdit);

  const { mutate, isPending } = useMutation({
    mutationFn: updateInfoRequest,
    onSuccess: (data) => {
      notification.success({ message: 'Cập nhật thông tin tài khoản thành công!' });
      setModalOpen(false);
      dispatch(setAuthUpdate(data?.data));
    },
    onError: (error) => {
      notification.error({ message: error.data?.message || 'Có lỗi xảy ra vui lòng thử lại sau!' });
    },
  });

  const items = [
    { label: 'First Name', children: dataRedux?.first_name },
    { label: 'Last Name', children: dataRedux?.last_name },
    { label: 'Tài khoản', children: dataRedux?.username },
    { label: 'Vị trí', children: dataRedux?.job || '-' },
    { label: 'Email', children: dataRedux?.email || '-' },
    { label: 'Số điện thoại', children: dataRedux?.phone || '-' },
    { label: 'Ngày sinh', children: dataRedux?.birthday ? dayjs(dataRedux.birthday).format('DD/MM/YYYY') : '-' },
  ].map((item) => ({ ...item, labelStyle: { width: 300 }, contentStyle: { fontSize: 15 } }));

  const fields = [
    {
      name: 'avatar',
      label: 'Ảnh đại diện',
      type: 'upload-cdn',
      propsInput: { listType: 'picture-card', className: 'custom' },
      icon: !avatar?.length ? (
        <button className="w-24 h-24" type="button">
          Tải lên
        </button>
      ) : null,
    },
    { name: 'first_name', label: 'First name', rules: [{ required: true }], lg: 12 },
    { name: 'last_name', label: 'Last name', rules: [{ required: true }], lg: 12 },
    { name: 'username', label: 'Username', propsInput: { disabled: true }, lg: 12 },
    { name: 'job', label: 'Vị trí', propsInput: { disabled: true }, lg: 12 },
    {
      name: 'email',
      label: 'Email',
      rules: [{ type: 'email', message: 'Email không hợp lệ. Vui lòng nhập lại!' }],
      lg: 12,
    },
    {
      name: 'phone',
      label: 'Số điện thoại',
      rules: [
        { pattern: /^(0[1-9]\d{8})$/, message: 'Số điện thoại không hợp lệ. Vui lòng nhập lại!' },
      ],
      lg: 12,
    },
    { name: 'birthday', label: 'Ngày sinh', type: 'date', lg: 12 },
    { name: 'change_password', label: '', labelCheckbox: 'Thay đổi mật khẩu', type: 'checkbox', lg: 12 },
    {
      name: 'password',
      label: 'Mật khẩu cũ',
      type: 'password',
      colClass: checkChangePassword ? '' : 'hidden',
      propsInput: { autoComplete: 'new-password' },
      rules: [{ required: checkChangePassword }],
      lg: 12,
    },
    {
      name: 'new_password',
      label: 'Mật khẩu mới',
      type: 'password',
      colClass: checkChangePassword ? '' : 'hidden',
      rules: [{ required: checkChangePassword }],
      lg: 12,
    },
  ];

  const handleOpenEdit = () => {
    formEdit.setFieldsValue({
      ...dataRedux,
      birthday: dataRedux?.birthday ? dayjs(dataRedux.birthday) : undefined,
      change_password: false,
      avatar: dataRedux?.avatar
        ? [{ uid: 1, name: 'avatar', status: 'done', url: dataRedux.avatar, response: dataRedux.avatar }]
        : [],
    });
    setModalOpen(true);
  };

  const onSubmitUpdate = (data) => {
    const checkUploading = data.avatar?.some((i) => i.status === 'uploading');
    if (checkUploading) {
      message.warning('File đang được upload vui lòng chờ trong giây lát!');
      return;
    }
    mutate({
      ...data,
      birthday: data.birthday && data.birthday.format('YYYY-MM-DD'),
      avatar: data.avatar?.[0]?.response,
    });
  };

  return (
    <>
      <div className="px-4 md:px-14 py-3">
        {/* Header card */}
        <div className="shadow shadow-gray-300 overflow-hidden rounded-lg">
          <div className="h-32 md:h-52 bg-gradient-to-r from-blue-500 to-indigo-600" />
          <div className="overflow-hidden -mt-16 md:-mt-20 px-4 md:px-7 flex flex-col gap-2 pb-3">
            <Avatar src={dataRedux?.avatar} icon={<UserOutlined />} size={120} shape="square" className="border-4 border-white" />
            <Typography.Title level={4} className="!mb-0">
              {`${dataRedux?.first_name || ''} ${dataRedux?.last_name || ''}`}
            </Typography.Title>
            {dataRedux?.job && (
              <Typography.Text type="secondary">{dataRedux.job}</Typography.Text>
            )}
          </div>
        </div>

        {/* Info card */}
        <Card
          title={<span className="uppercase">Thông tin tài khoản</span>}
          extra={
            <Button onClick={handleOpenEdit} className="border-sky-500 bg-white text-sky-500">
              Cập nhật thông tin
            </Button>
          }
          className="shadow shadow-gray-300 mt-6"
        >
          <Descriptions column={1} items={items} />
        </Card>

        {/* Seed Permissions - tạo nhanh tất cả quyền (Chỉ Super Admin mới thấy) */}
        {/* {dataRedux?.role === 9999 && <SeedPermissions />} */}
      </div>

      <Modal
        title="Cập nhật thông tin tài khoản"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <RenderForm
          loadingSubmit={isPending}
          onFinish={onSubmitUpdate}
          onCancel={()=>{setModalOpen(false)}}
          fields={fields}
          form={formEdit}
        />
      </Modal>
    </>
  );
};

export default Info;
